/**
 * useListingIntelligenceData — Data hook for the Listing Intelligence page.
 *
 * Fetches data from:
 *  - Listings list: apiClient.getListings()
 *  - Listing audit: apiClient.getListingAudit() (scores, issues, alignment)
 *  - Review intelligence: apiClient.getReviewIntelligence() (fallback alignment)
 *  - Season profile: apiClient.getSeasonProfile() (seasonal section)
 */

import { useEffect, useState, useCallback, useMemo } from "react"
import { apiClient } from "@/lib/api"
import type {
  ReviewIntelligenceResponse,
  SeasonProfileResponse,
  ListingAuditResponse,
  ListingAuditData,
  PhotoAnalysisResult,
} from "@/lib/api"
import type {
  ListingTab,
  DimensionScore,
  AuditIssue,
  AlignmentRow,
  SeasonalMismatch,
  LetterGrade,
  ScoreLabel,
} from "./listing-data"

// ---------------------------------------------------------------------------
// Types for raw API data
// ---------------------------------------------------------------------------

interface RawListing {
  id: string
  name: string
  pms_unit_ref?: string | null
  is_default?: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function letterGrade(score: number): LetterGrade {
  if (score >= 80) return "A"
  if (score >= 60) return "B"
  if (score >= 40) return "C"
  return "D"
}

function scoreLabel(score: number): ScoreLabel {
  if (score >= 80) return "Good"
  if (score >= 60) return "Needs Work"
  if (score >= 40) return "Poor"
  return "Critical"
}

function currentSeasonName(): string {
  const m = new Date().getMonth() + 1
  if (m >= 3 && m <= 5) return "Spring"
  if (m >= 6 && m <= 8) return "Summer"
  if (m >= 9 && m <= 11) return "Autumn"
  return "Winter"
}

function nextSeasonInfo(): { name: string; date: string; emphasis: string } | null {
  const m = new Date().getMonth() + 1
  if (m === 2 || m === 3) return { name: "Spring", date: "March 20", emphasis: "Shift to hiking trails, wildflowers, outdoor dining, and longer daylight hours." }
  if (m === 5 || m === 6) return { name: "Summer", date: "June 21", emphasis: "Highlight beach access, outdoor activities, warm evenings, and long daylight." }
  if (m === 8 || m === 9) return { name: "Autumn", date: "September 22", emphasis: "Feature autumn colours, cosy interiors, harvest events, and shoulder-season value." }
  if (m === 11 || m === 12) return { name: "Winter", date: "December 21", emphasis: "Emphasise fireside comfort, winter activities, festive atmosphere, and indoor amenities." }
  return null
}

// ---------------------------------------------------------------------------
// Build dimensions from audit data
// ---------------------------------------------------------------------------

function buildDimensionsFromAudit(audit: ListingAuditData): DimensionScore[] {
  const dims = audit.dimensions
  return [
    { key: "content", label: "Content Quality", score: dims.content.score, max: 100, scoreLabel: dims.content.label as ScoreLabel, description: dims.content.description },
    { key: "amenities", label: "Amenity Completeness", score: dims.amenities.score, max: 100, scoreLabel: dims.amenities.label as ScoreLabel, description: dims.amenities.description },
    { key: "reviews", label: "Review Alignment", score: dims.reviews.score, max: 100, scoreLabel: dims.reviews.label as ScoreLabel, description: dims.reviews.description },
    { key: "seasonal", label: "Seasonal Relevance", score: dims.seasonal.score, max: 100, scoreLabel: dims.seasonal.label as ScoreLabel, description: dims.seasonal.description },
    { key: "photos", label: "Photo Assessment", score: dims.photo.score, max: 100, scoreLabel: dims.photo.label as ScoreLabel, description: dims.photo.description },
  ]
}

function buildPlaceholderDimensions(): DimensionScore[] {
  return [
    { key: "content", label: "Content Quality", score: 0, max: 100, scoreLabel: "Critical", description: "Run an audit to assess your listing content quality." },
    { key: "amenities", label: "Amenity Completeness", score: 0, max: 100, scoreLabel: "Critical", description: "Run an audit to check amenity coverage." },
    { key: "reviews", label: "Review Alignment", score: 0, max: 100, scoreLabel: "Critical", description: "Run an audit to compare listing claims with guest reviews." },
    { key: "seasonal", label: "Seasonal Relevance", score: 0, max: 100, scoreLabel: "Critical", description: "Run an audit to check seasonal content relevance." },
    { key: "photos", label: "Photo Assessment", score: 0, max: 100, scoreLabel: "Critical", description: "No photo data available." },
  ]
}

// ---------------------------------------------------------------------------
// Build issues from audit data
// ---------------------------------------------------------------------------

function buildIssuesFromAudit(audit: ListingAuditData): AuditIssue[] {
  return (audit.issues || []).map((issue) => ({
    id: issue.id,
    severity: issue.severity,
    category: issue.category,
    title: issue.title,
    description: issue.description,
    source: issue.source,
    fixable: issue.fixType != null,
    fixType: issue.fixType ?? undefined,
  }))
}

// ---------------------------------------------------------------------------
// Build alignment rows from audit or review intelligence fallback
// ---------------------------------------------------------------------------

function buildAlignmentFromAudit(audit: ListingAuditData): AlignmentRow[] {
  return (audit.alignment || []).map((row) => ({
    claim: row.claim,
    evidence: row.evidence,
    status: row.status,
  }))
}

function buildAlignmentFromReviews(
  intel: ReviewIntelligenceResponse | null,
): AlignmentRow[] {
  if (!intel?.available || !intel.analysis) return []
  const analysis = intel.analysis
  const rows: AlignmentRow[] = []

  for (const s of (analysis.strengths || []).slice(0, 3)) {
    rows.push({
      claim: s.theme,
      evidence: s.signal || `Consistently praised by guests`,
      status: "confirmed",
    })
  }

  for (const r of (analysis.risks || []).slice(0, 2)) {
    rows.push({
      claim: r.theme,
      evidence: r.evidence || `Mentioned as an area of concern by guests`,
      status: "unconfirmed",
    })
  }

  const lowConfidence = (analysis.strengths || []).filter(s => s.confidence === "low").slice(0, 2)
  for (const h of lowConfidence) {
    rows.push({
      claim: h.theme,
      evidence: h.signal || `Guests mention this but it may not be in your listing`,
      status: "missed",
    })
  }

  return rows
}

// ---------------------------------------------------------------------------
// Build seasonal data from audit or season profile fallback
// ---------------------------------------------------------------------------

function buildSeasonalEmphasis(sp: SeasonProfileResponse | null): string {
  if (!sp) return ""
  const season = currentSeasonName().toLowerCase()
  if (sp.classification === "year_round") {
    return "Your property performs consistently year-round. Keep content balanced across all seasons."
  }
  const isPeak = sp.peak_months?.includes(new Date().getMonth() + 1)
  if (isPeak) {
    return `This is your peak season. Emphasise your strongest features and ensure availability is maximised.`
  }
  const isOff = sp.off_season_months?.includes(new Date().getMonth() + 1)
  if (isOff) {
    return `Off-season is active. Shift content to indoor comfort, value pricing, and unique ${season} experiences.`
  }
  return `Shoulder season is active. Balance content between seasonal activities and year-round amenities.`
}

function buildSeasonalMismatches(
  audit: ListingAuditData | null,
  sp: SeasonProfileResponse | null,
): SeasonalMismatch[] {
  // Prefer audit mismatches (from LLM analysis)
  if (audit?.seasonal_mismatches && audit.seasonal_mismatches.length > 0) {
    return audit.seasonal_mismatches.map((m) => ({ text: m.text }))
  }
  // Fallback to basic heuristic
  if (!sp) return []
  const mismatches: SeasonalMismatch[] = []
  const m = new Date().getMonth() + 1
  const isOff = sp.off_season_months?.includes(m)
  if (isOff) {
    mismatches.push({ text: "You may be in off-season. Check that your listing description reflects current seasonal activities." })
  }
  return mismatches
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface ListingIntelligenceState {
  // Page-level state
  loading: boolean
  error: string
  setError: (e: string) => void

  // Listing tabs
  listings: ListingTab[]
  activeListingId: string
  setActiveListingId: (id: string) => void

  // Audit state
  hasAudit: boolean
  auditSummary: string

  // Hero
  compositeScore: number
  grade: LetterGrade
  scoreDelta: number | null
  lastAuditedAt: string | null

  // Dimensions
  dimensions: DimensionScore[]

  // Issues
  issues: AuditIssue[]

  // Review alignment
  hasReviews: boolean
  alignment: AlignmentRow[]

  // Seasonal
  hasSeasonProfiles: boolean
  currentSeason: string
  seasonalEmphasis: string
  upcomingSeason: { name: string; date: string; emphasis: string } | null
  seasonalMismatches: SeasonalMismatch[]

  // Photo analysis
  photoAnalysis: PhotoAnalysisResult | null
  hasPhotoAnalysis: boolean
  isAnalysingPhotos: boolean
  runPhotoAnalysis: () => Promise<void>

  // Actions
  refresh: () => Promise<void>
  runAudit: () => Promise<void>
  isAuditing: boolean

  // Daily run cooldowns
  auditCooldown: boolean
  photoCooldown: boolean
}

export function useListingIntelligenceData(
  propertyId: string | undefined,
): ListingIntelligenceState {
  const [listings, setListings] = useState<ListingTab[]>([])
  const [activeListingId, setActiveListingId] = useState("")
  const [intel, setIntel] = useState<ReviewIntelligenceResponse | null>(null)
  const [seasonProfile, setSeasonProfile] = useState<SeasonProfileResponse | null>(null)
  const [audit, setAudit] = useState<ListingAuditData | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuditing, setIsAuditing] = useState(false)
  const [isAnalysingPhotos, setIsAnalysingPhotos] = useState(false)
  const [error, setError] = useState("")

  const loadData = useCallback(async () => {
    if (!propertyId) return
    setLoading(true)
    try {
      const [listingsRes, auditRes, intelRes, seasonRes] = await Promise.allSettled([
        apiClient.getListings(propertyId),
        apiClient.getListingAudit(propertyId),
        apiClient.getReviewIntelligence(propertyId),
        apiClient.getSeasonProfile(propertyId),
      ])

      // Listings
      if (listingsRes.status === "fulfilled" && listingsRes.value.ok && listingsRes.value.listings) {
        const raw = listingsRes.value.listings as unknown as RawListing[]
        const tabs: ListingTab[] = raw.map((l) => ({
          id: l.id,
          name: l.name || "Listing",
          grade: "B" as LetterGrade,
        }))
        setListings(tabs)
        if (!activeListingId && tabs.length > 0) {
          setActiveListingId(tabs[0].id)
        }
      }

      // Audit data
      if (auditRes.status === "fulfilled" && auditRes.value.ok && auditRes.value.available && auditRes.value.audit) {
        setAudit(auditRes.value.audit)
        // Update listing tab grades from audit
        if (listingsRes.status === "fulfilled" && listingsRes.value.ok && listingsRes.value.listings) {
          const auditGrade = auditRes.value.audit.letter_grade as LetterGrade
          const raw = listingsRes.value.listings as unknown as RawListing[]
          const tabs: ListingTab[] = raw.map((l) => ({
            id: l.id,
            name: l.name || "Listing",
            grade: auditGrade,
          }))
          setListings(tabs)
        }
      } else {
        setAudit(null)
      }

      // Review intelligence (for alignment fallback)
      if (intelRes.status === "fulfilled" && intelRes.value.ok) {
        setIntel(intelRes.value)
      }

      // Season profile (for seasonal section)
      if (seasonRes.status === "fulfilled" && seasonRes.value.ok) {
        setSeasonProfile(seasonRes.value)
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }, [propertyId, activeListingId])

  const runAudit = useCallback(async () => {
    if (!propertyId || isAuditing) return
    setIsAuditing(true)
    setError("")
    try {
      const res = await apiClient.runListingAudit(propertyId)
      if (res.ok && res.audit) {
        setAudit(res.audit)
      } else if (res.error) {
        setError(res.error)
      } else if (res.ok && !res.audit) {
        setError("No data available to run an audit. Import Airbnb reviews first.")
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setIsAuditing(false)
    }
  }, [propertyId, isAuditing])

  const runPhotoAnalysis = useCallback(async () => {
    if (!propertyId || isAnalysingPhotos) return
    setIsAnalysingPhotos(true)
    setError("")
    try {
      const res = await apiClient.runPhotoAnalysis(propertyId)
      if (res.ok && res.photo_analysis) {
        // Update the audit with the new photo analysis
        setAudit((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            photo_analysis: res.photo_analysis,
            dimensions: {
              ...prev.dimensions,
              photo: {
                score: res.photo_analysis!.photo_score,
                label: res.photo_analysis!.photo_label as "Good" | "Needs Work" | "Poor" | "Critical",
                description: res.photo_analysis!.photo_summary,
              },
            },
          }
        })
      } else if (res.error) {
        setError(res.error)
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setIsAnalysingPhotos(false)
    }
  }, [propertyId, isAnalysingPhotos])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Derived state — prefer audit data, fallback to review intelligence / season profile
  const hasAudit = audit != null
  const hasReviews = intel?.available === true && !!intel.analysis
  const hasSeasonProfiles = seasonProfile != null

  // Scores
  const compositeScore = audit?.composite_score ?? 0
  const grade: LetterGrade = hasAudit ? (audit.letter_grade as LetterGrade) : "D"
  const scoreDelta = audit?.score_delta ?? null
  const lastAuditedAt = audit?.computed_at ?? null
  const auditSummary = audit?.audit_summary ?? ""

  // Dimensions — from audit or placeholder
  const dimensions = hasAudit ? buildDimensionsFromAudit(audit) : buildPlaceholderDimensions()

  // Issues — from audit or empty
  const issues = hasAudit ? buildIssuesFromAudit(audit) : []

  // Alignment — from audit or review intelligence fallback
  const alignment = hasAudit && audit.alignment?.length > 0
    ? buildAlignmentFromAudit(audit)
    : buildAlignmentFromReviews(intel)

  // Seasonal
  const currentSeason = currentSeasonName()
  const seasonalEmphasis = buildSeasonalEmphasis(seasonProfile)
  const upcomingSeason = nextSeasonInfo()
  const seasonalMismatches = buildSeasonalMismatches(audit, seasonProfile)

  // Photo analysis
  const photoAnalysis = audit?.photo_analysis ?? null
  const hasPhotoAnalysis = photoAnalysis != null && typeof photoAnalysis.photo_score === "number"

  // Daily run cooldown — disable re-run buttons for 24h after last run
  const DAILY_COOLDOWN_MS = 24 * 60 * 60 * 1000
  const auditCooldown = useMemo(() => {
    const computedAt = audit?.computed_at
    if (!computedAt) return false
    const ts = new Date(computedAt).getTime()
    if (Number.isNaN(ts)) return false
    return Date.now() - ts < DAILY_COOLDOWN_MS
  }, [audit?.computed_at])

  const photoCooldown = useMemo(() => {
    const analysedAt = photoAnalysis?.analysed_at
    if (!analysedAt) return false
    const ts = new Date(analysedAt).getTime()
    if (Number.isNaN(ts)) return false
    return Date.now() - ts < DAILY_COOLDOWN_MS
  }, [photoAnalysis?.analysed_at])

  return {
    loading,
    error,
    setError,
    listings,
    activeListingId,
    setActiveListingId,
    hasAudit,
    auditSummary,
    compositeScore,
    grade,
    scoreDelta,
    lastAuditedAt,
    dimensions,
    issues,
    hasReviews,
    alignment,
    hasSeasonProfiles,
    currentSeason,
    seasonalEmphasis,
    upcomingSeason,
    seasonalMismatches,
    photoAnalysis,
    hasPhotoAnalysis,
    isAnalysingPhotos,
    runPhotoAnalysis,
    refresh: loadData,
    runAudit,
    isAuditing,
    auditCooldown,
    photoCooldown,
  }
}
